/** @jsxImportSource @emotion/react */
import { EmptyData } from '@app/components/ui/EmptyData/EmptyData'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { DatabaseIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { DatasetRow } from '@app/modules/Home/Data/types'
import { useFetchDatasets } from '@app/modules/Home/Data/hooks/useFetchDatasets'
import * as styles from './DataLayerTable.styles'
import { formatDate } from '../utils/data.submodule.utils'

const columns: ColumnDef<DatasetRow>[] = [
  {
    id: 'name',
    header: 'Name',
    accessorFn: (row) => row.Name ?? '',
    enableSorting: true,
  },
  {
    id: 'description',
    header: 'Description',
    accessorFn: (row) => row.Comments ?? '',
    enableSorting: true,
  },
  {
    id: 'uploadedBy',
    header: 'Uploaded By',
    accessorFn: (row) => row['Uploaded By'] ?? '',
    enableSorting: true,
  },
  {
    id: 'dateAdded',
    header: 'Date Added',
    accessorFn: (row) => row['Date Added'] ?? '',
    cell: ({ getValue }) => formatDate(getValue<string>()),
    enableSorting: true,
  },
  {
    id: 'dateUpdated',
    header: 'Date Updated',
    accessorFn: (row) => row['Date Updated'] ?? '',
    cell: ({ getValue }) => formatDate(getValue<string>()),
    enableSorting: true,
  },
  {
    id: 'geometryType',
    header: 'Geometry Type',
    accessorFn: (row) => row['Geometry Type'] ?? '',
    enableSorting: true,
  },
]

interface DatasetListProps {
  onSelect: (row: DatasetRow) => void
}

const DatasetList = ({ onSelect }: DatasetListProps) => {
  const { data: datasets, isLoading, isError } = useFetchDatasets()
  const [sorting, setSorting] = useState<SortingState>([{ id: 'dateUpdated', desc: true }])
  const [globalFilter, setGlobalFilter] = useState('')
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(20)

  const data = useMemo(() => datasets ?? [], [datasets])

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination: { pageIndex, pageSize },
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater
      setPageIndex(next.pageIndex)
      setPageSize(next.pageSize)
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: 'includesString',
    enableSortingRemoval: false,
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return (
      <Typography color="error.main" sx={{ py: 2 }}>
        Failed to load datasets. Please try again.
      </Typography>
    )
  }

  return (
    <Paper css={styles.tableWrapper}>
      <Box css={styles.toolbar}>
        <TextField
          size="small"
          placeholder="Search datasets"
          value={globalFilter}
          onChange={(e) => {
            setGlobalFilter(e.target.value)
            setPageIndex(0)
          }}
          css={styles.searchInput}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer css={styles.tableContainer}>
        <Table stickyHeader aria-label="datasets table">
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const sortDir = header.column.getIsSorted()
                  const active = sortDir !== false
                  const direction: 'asc' | 'desc' = sortDir === 'desc' ? 'desc' : 'asc'
                  return (
                    <TableCell
                      key={header.id}
                      css={styles.headerCell}
                      sortDirection={active ? direction : false}
                    >
                      {canSort ? (
                        <TableSortLabel
                          active={active}
                          direction={direction}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </TableSortLabel>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} css={styles.emptyRow}>
                  <EmptyData title="No datasets found" icon={<DatabaseIcon />} />
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.id}
                  onClick={() => onSelect(row.original)}
                  sx={{ cursor: 'pointer' }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={table.getFilteredRowModel().rows.length}
        rowsPerPage={pageSize}
        page={pageIndex}
        onPageChange={(_e, newPage) => table.setPageIndex(newPage)}
        onRowsPerPageChange={(e) => table.setPageSize(parseInt(e.target.value, 10))}
      />
    </Paper>
  )
}

export default DatasetList
